'use client';

import { useState, useMemo } from 'react';
import {
  Search,
  Plus,
  Eye,
  Edit2,
  ListTodo,
  Clock,
  User,
  Flag,
  Calendar,
} from 'lucide-react';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { tasks, projects } from '../data/mock-data';
import { taskStatusConfig, taskPriorityConfig, type Task } from '../types';

// 작업 등록 모달
function TaskAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="작업 등록"
      description="새로운 프로젝트 작업을 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="작업 코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="작업명" required>
            <FormInput placeholder="작업명을 입력하세요" />
          </FormField>
          <FormField label="프로젝트" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="담당자" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김영업</option>
              <option value="u002">이마케팅</option>
              <option value="u003">박관리</option>
              <option value="u004">최개발</option>
              <option value="u005">정디자인</option>
            </FormSelect>
          </FormField>
          <FormField label="시작일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="종료일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="상태" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(taskStatusConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="우선순위" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(taskPriorityConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="예상 시간">
            <FormInput type="number" min={0} step={0.5} placeholder="0" />
          </FormField>
          <FormField label="완료율 (%)">
            <FormInput type="number" min={0} max={100} placeholder="0" />
          </FormField>
        </div>
        <FormField label="작업 설명">
          <FormTextarea rows={3} placeholder="작업에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function TaskListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return tasks.filter((task) => {
      const matchesSearch =
        task.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.projectName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || task.status === selectedStatus;
      const matchesProject = selectedProject === 'all' || task.projectId === selectedProject;
      const matchesPriority = selectedPriority === 'all' || task.priority === selectedPriority;

      return matchesSearch && matchesStatus && matchesProject && matchesPriority;
    });
  }, [searchQuery, selectedStatus, selectedProject, selectedPriority]);

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    tasks.forEach((task) => {
      stats[task.status] = (stats[task.status] || 0) + 1;
    });
    return stats;
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.gray;
  };

  const getPriorityIcon = (priority: string) => {
    const priorityConfig = taskPriorityConfig[priority as keyof typeof taskPriorityConfig];
    const colors: Record<string, string> = {
      gray: 'text-gray-400',
      blue: 'text-blue-500',
      orange: 'text-orange-500',
      red: 'text-red-500',
    };
    return colors[priorityConfig?.color || 'gray'];
  };

  // 시간 진행률 계산
  const getTimeProgress = (actual: number, expected: number) => {
    if (expected === 0) return 0;
    return Math.min((actual / expected) * 100, 100);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="작업 관리"
        description="프로젝트 작업을 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            작업 등록
          </button>
        }
      />

      {/* 상태별 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(taskStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status] || 0}개</div>
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="작업 코드, 이름, 프로젝트명 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 프로젝트</option>
          {projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(taskStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        <select
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 우선순위</option>
          {Object.entries(taskPriorityConfig).map(([priority, config]) => (
            <option key={priority} value={priority}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 작업 목록 */}
      <div className="space-y-3">
        {filteredData.map((task) => {
          const statusConfig = taskStatusConfig[task.status];
          const priorityConfig = taskPriorityConfig[task.priority];
          const timeProgress = getTimeProgress(task.actualHours, task.expectedHours);
          const isOvertime = task.actualHours > task.expectedHours;

          return (
            <div
              key={task.id}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    <ListTodo className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-primary">{task.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                        {statusConfig.label}
                      </span>
                      <Flag className={`w-4 h-4 ${getPriorityIcon(task.priority)}`} />
                      <span className="text-xs text-muted-foreground">{priorityConfig.label}</span>
                    </div>
                    <div className="font-medium mt-1">{task.name}</div>
                    <div className="text-sm text-muted-foreground">{task.projectName}</div>

                    {/* 메타 정보 */}
                    <div className="flex flex-wrap gap-4 mt-2 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>{task.assignedToName}</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span>{task.startDate} ~ {task.endDate}</span>
                      </div>
                      <div className={`flex items-center gap-1 ${isOvertime ? 'text-red-500' : 'text-muted-foreground'}`}>
                        <Clock className="w-4 h-4" />
                        <span>{task.actualHours}h / {task.expectedHours}h</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button className="p-1.5 rounded hover:bg-accent" title="보기">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button className="p-1.5 rounded hover:bg-accent" title="수정">
                    <Edit2 className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </div>

              {/* 시간 진행률 바 */}
              <div className="mt-3">
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      isOvertime
                        ? 'bg-red-500'
                        : timeProgress > 80
                        ? 'bg-yellow-500'
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${timeProgress}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          검색 결과가 없습니다.
        </div>
      )}

      {/* 작업 등록 모달 */}
      <TaskAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
